import { Link, useNavigate } from "@tanstack/react-router";
import { Heart, User, LogOut, Shield, Menu, X, Moon, Sun, ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useRoles } from "@/hooks/use-roles";
import { useTheme } from "@/hooks/use-theme";
import { NotificationsBell } from "@/components/notifications-bell";
import pluggzLogo from "@/assets/pluggz-logo.png";

export function SiteHeader() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { isAdmin, isCreator, loading: rolesLoading } = useRoles();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const close = () => setOpen(false);
  const showApply = !user || (!isCreator && !isAdmin);

  return (
    <header role="banner" className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
        <nav className="hidden items-center gap-8 text-sm md:flex">
          <Link to="/discover" className="text-muted-foreground hover:text-foreground transition">Discover</Link>
          <Link to="/creators" className="text-muted-foreground hover:text-foreground transition">Creators</Link>
          <Link to="/leaderboard" className="text-muted-foreground hover:text-foreground transition">Leaderboard</Link>
          <Link to="/journal" className="text-muted-foreground hover:text-foreground transition">Journal</Link>
          {showApply && (
            <Link to="/apply" className="text-muted-foreground hover:text-foreground transition">Apply</Link>
          )}
        </nav>
        <Link to="/" aria-label="Pluggz home" className="flex items-center">
          <img
            src={pluggzLogo}
            alt="Pluggz"
            width={120}
            height={28}
            className="h-9 w-auto dark:invert"
            onError={(e) => {
              e.currentTarget.style.display = "none";
              const fallback = e.currentTarget.nextElementSibling as HTMLElement | null;
              if (fallback) fallback.classList.remove("hidden");
            }}
          />
          <span className="hidden font-serif text-xl tracking-tight">Pluggz.</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/wishlist" aria-label="Wishlist" className="p-2 text-muted-foreground hover:text-foreground"><Heart className="h-4 w-4" /></Link>
          <button
            type="button"
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            onClick={toggleTheme}
            className="p-2 text-muted-foreground hover:text-foreground"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user ? (
            <>
              <NotificationsBell />
              {isAdmin && !rolesLoading ? (
                <Link
                  to="/admin"
                  aria-label="Admin console"
                  className="hidden md:inline-flex items-center gap-1.5 border border-foreground/80 px-2.5 py-1 text-[11px] uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background transition-colors"
                >
                  <Shield className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Admin</span>
                </Link>
              ) : isCreator && !rolesLoading ? (
                <Link to="/creator-dashboard" aria-label="Studio" className="hidden md:inline p-2 text-muted-foreground hover:text-foreground">
                  <User className="h-4 w-4" />
                </Link>
              ) : !rolesLoading ? (
                <Link to="/shopper-profile" aria-label="Your profile" className="hidden md:inline p-2 text-muted-foreground hover:text-foreground">
                  <User className="h-4 w-4" />
                </Link>
              ) : null}
              <button
                aria-label="Sign out"
                onClick={async () => { await signOut(); navigate({ to: "/" }); }}
                className="hidden md:inline p-2 text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline text-muted-foreground hover:text-foreground">Sign in</Link>
              <Link
                to="/signup"
                className="hidden md:inline-flex items-center border border-foreground/80 px-2.5 py-1 text-[11px] uppercase tracking-widest text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Sign up
              </Link>
            </>
          )}
          <button
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex md:hidden p-2 text-muted-foreground hover:text-foreground"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div
          ref={menuRef}
          className="md:hidden border-t border-border/60 bg-background"
        >
          <nav className="flex flex-col px-6 py-4 text-sm">
            <Link to="/discover" onClick={close} className="py-2 text-foreground hover:text-muted-foreground">Discover</Link>
            <Link to="/creators" onClick={close} className="py-2 text-foreground hover:text-muted-foreground">Creators</Link>
            <Link to="/leaderboard" onClick={close} className="py-2 text-foreground hover:text-muted-foreground">Leaderboard</Link>
            <Link to="/journal" onClick={close} className="py-2 text-foreground hover:text-muted-foreground">Journal</Link>
            {showApply && (
              <Link to="/apply" onClick={close} className="py-2 text-foreground hover:text-muted-foreground">Apply</Link>
            )}
            <div className="mt-2 border-t border-border/60 pt-2">
              <button
                type="button"
                onClick={() => setCategoriesOpen((v) => !v)}
                className="flex w-full items-center justify-between py-2 text-left text-xs uppercase tracking-widest text-muted-foreground"
              >
                <span>Categories</span>
                {categoriesOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </button>
              {categoriesOpen && (
                <div className="flex flex-col pb-1">
                  <Link to="/category/womens-fashion" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Women's Fashion</Link>
                  <Link to="/category/beauty-skincare" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Beauty & Skincare</Link>
                  <Link to="/category/mother-baby" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Mother & Baby</Link>
                  <Link to="/category/home-decor" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Home & Decor</Link>
                  <Link to="/category/fitness-wellness" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Fitness & Wellness</Link>
                  <Link to="/category/shoes-accessories" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Shoes & Accessories</Link>
                  <Link to="/category/cooking-grocery" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Cooking & Grocery</Link>
                  <Link to="/category/finance-lifestyle" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Finance & Lifestyle</Link>
                  <Link to="/category/travel" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">Travel</Link>
                  <Link to="/category/grwm" onClick={close} className="py-1.5 text-sm text-foreground hover:text-muted-foreground">GRWM</Link>
                </div>
              )}
            </div>
            <button
              type="button"
              aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              onClick={() => { toggleTheme(); }}
              className="flex w-full items-center gap-2 py-2 text-left text-foreground hover:text-muted-foreground"
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === "dark" ? "Light mode" : "Dark mode"}</span>
            </button>
            <div className="mt-2 border-t border-border/60 pt-2">
              {user ? (
                <>
                  {isAdmin && !rolesLoading && (
                    <Link to="/admin" onClick={close} className="flex items-center gap-2 py-2 text-foreground hover:text-muted-foreground">
                      <Shield className="h-4 w-4" /> Admin
                    </Link>
                  )}
                  {isCreator && !rolesLoading && (
                    <Link to="/creator-dashboard" onClick={close} className="flex items-center gap-2 py-2 text-foreground hover:text-muted-foreground">
                      <User className="h-4 w-4" /> Dashboard
                    </Link>
                  )}
                  {!isAdmin && !isCreator && !rolesLoading && (
                    <Link to="/shopper-profile" onClick={close} className="flex items-center gap-2 py-2 text-foreground hover:text-muted-foreground">
                      <User className="h-4 w-4" /> Profile
                    </Link>
                  )}
                  <button
                    onClick={async () => { close(); await signOut(); navigate({ to: "/" }); }}
                    className="flex w-full items-center gap-2 py-2 text-left text-foreground hover:text-muted-foreground"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={close} className="block py-2 text-foreground hover:text-muted-foreground">Sign in</Link>
                  <Link to="/signup" onClick={close} className="block py-2 text-foreground hover:text-muted-foreground">Sign up</Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
